import { useId, useState } from "react";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useProvider } from "../providers/ProviderContext";

const ADD_PROVIDER = "__add_provider__";

function providerHost(baseUrl: string): string {
  try {
    return new URL(baseUrl).host;
  } catch {
    return baseUrl;
  }
}

/** MUI provider picker shared by the authenticated header and provider login. */
export function ProviderSwitcher({ fullWidth = false }: { fullWidth?: boolean }) {
  const { deployment, registry, activeConnection, setActiveProviderId, addProvider } = useProvider();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const labelId = useId();

  if (!deployment.showSwitcher) return null;

  const handleChange = async (event: SelectChangeEvent) => {
    const id = event.target.value;
    if (id === ADD_PROVIDER) {
      setError(null);
      setDialogOpen(true);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await setActiveProviderId(id);
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const handleAdd = async () => {
    setBusy(true);
    setError(null);
    try {
      await addProvider({ label, baseUrl });
      setLabel("");
      setBaseUrl("");
      setDialogOpen(false);
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <FormControl size="small" fullWidth={fullWidth} sx={{ minWidth: fullWidth ? undefined : 220 }}>
        <InputLabel id={labelId}>Provider</InputLabel>
        <Select
          labelId={labelId}
          label="Provider"
          value={activeConnection?.id ?? ""}
          onChange={(event) => void handleChange(event)}
          disabled={busy}
          renderValue={(id) => {
            const connection = registry.connections.find((candidate) => candidate.id === id);
            return connection ? `${connection.label} · ${providerHost(connection.baseUrl)}` : "Provider";
          }}
          sx={{ maxWidth: fullWidth ? undefined : 340 }}
        >
          {registry.connections.map((connection) => (
            <MenuItem key={connection.id} value={connection.id}>
              <ListItemText
                primary={connection.label}
                secondary={providerHost(connection.baseUrl)}
                slotProps={{ primary: { noWrap: true }, secondary: { noWrap: true } }}
              />
            </MenuItem>
          ))}
          <Divider />
          <MenuItem value={ADD_PROVIDER}>
            <ListItemIcon><AddCircleOutlineIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Add provider" />
          </MenuItem>
        </Select>
        {error && !dialogOpen ? <FormHelperText error>{error}</FormHelperText> : null}
      </FormControl>

      <Dialog open={dialogOpen} onClose={() => !busy && setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add provider</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Provider metadata is checked before this connection becomes active. Sign-in remains separate for every provider.
            </Typography>
            <TextField
              label="Label"
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              placeholder="Europe"
              autoFocus
              fullWidth
            />
            <TextField
              label="Provider base URL"
              value={baseUrl}
              onChange={(event) => setBaseUrl(event.target.value)}
              placeholder="https://eu-api.example.org"
              type="url"
              fullWidth
            />
            {error ? <Box><FormHelperText error>{error}</FormHelperText></Box> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={busy}>Cancel</Button>
          <Button variant="contained" onClick={() => void handleAdd()} disabled={busy || !label.trim() || !baseUrl.trim()}>
            {busy ? "Connecting…" : "Add and switch"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
